/*
  OFFSET spend rewards: puts the free gift in the cart, and takes it back out.

  Xavier, 26 Sep 2026. The gift products are priced at zero, with the real
  value as their compare at price, so the cart shows "$0, was $14.99" without
  any discount being involved. That matters: Shopify runs only ONE automatic
  Buy X get Y per order, and two gift tiers could never both fire through it.
  Zero-priced products have no such limit, and it leaves the Buy X get Y slot
  free for a real offer later.

  The trade is that nothing server side now checks the threshold, so this
  script is the gate. It removes any gift line the cart has not earned, even
  one added by hand.

  They agree because both read the same list: the ladder written in
  snippets/offset-reward-bar.liquid, which that snippet prints onto the bar as
  data-tiers. Nothing is duplicated here.

  Safety rules this follows:
    - Only lines carrying the _reward property are ever removed. A pad the
      shopper chose themselves is theirs, it is never touched.
    - A gift line never counts towards a threshold, so the ladder cannot climb
      itself. The sum is done here from /cart.js rather than read off the bar,
      because the drawer markup can be a redraw behind the real cart and a
      stale number would hand out the wrong gift.
    - Nothing happens at all unless the bar says data-gifts="on". Until the
      discounts are live, adding a gift would charge full price for it.
    - One request at a time, and it re-reads the cart after each change rather
      than assuming.
*/
(function () {
  'use strict';

  var GIFT_PROP = '_reward';
  var busy = false;
  var again = false;

  /* The drawn bar when there is one, otherwise the always-present config
     element beside it. Both carry data-tiers and data-gifts; only the bar
     carries data-spend, which nothing here reads, because spend is worked out
     from the cart. Xavier, 7 Oct 2026. */
  function bar() {
    return document.querySelector('[data-reward-bar]') ||
           document.querySelector('[data-reward-config]');
  }

  /* "100|Free pads|52870221725983§150|Free shipping|" -> usable objects */
  function ladder(el) {
    var raw = el.getAttribute('data-tiers') || '';
    return raw.split('§').map(function (chunk) {
      var p = chunk.split('|');
      return {
        cents: Math.round(parseFloat(p[0] || '0') * 100),
        label: p[1] || '',
        variant: (p[2] || '').trim(),
        /* "replaces" means this gift already contains every product gift
           below it, so earning it retires them. See the note on wanted(). */
        replaces: (p[4] || '').trim() === 'replaces'
      };
    }).filter(function (t) { return t.cents > 0 && t.variant; });
  }

  /* One flaky read must not cost somebody their gift. Shopify answers a
     rate-limited or hiccupping request with an HTML page, and .json() on that
     throws "Unexpected token '<'", which used to take the whole engine down
     for the rest of the page view. Two quick retries, then give up honestly.
     Xavier, 7 Oct 2026. */
  function getCart(tries) {
    tries = tries || 0;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        if (tries >= 2) throw err;
        return new Promise(function (go) { setTimeout(go, 400 * (tries + 1)); })
          .then(function () { return getCart(tries + 1); });
      });
  }

  /* Everything except the gifts we put there. Shopify gives line_price before
     line-level discounts and final_line_price after, and cart.js calls the
     latter discounted_price, which is what the shopper is really paying. */
  function spendable(cart) {
    var osp = document.querySelector('[data-shipping-protection]');
    var ospId = osp ? Number(osp.getAttribute('data-variant')) : 0;
    return cart.items.reduce(function (sum, i) {
      if (i.properties && i.properties[GIFT_PROP]) return sum;
      if (ospId && i.variant_id === ospId) return sum;
      return sum + (typeof i.discounted_line_price === 'number' ? i.discounted_line_price : i.line_price);
    }, 0);
  }

  /* The property, and nothing but the property. Xavier, 7 Oct 2026.

     This used to match on variant id as well, which was right while the gifts
     were their own hidden $0 products. They are not any more. The gift is now
     the REAL pads and the REAL case, discounted to nothing by an automatic
     Buy X Get Y rule, and those are products anybody can simply buy. Matching
     on the id would have read a shopper's five paid pads as five free ones
     and clamped four of them out of their cart. The property is the only
     thing that says "we put this here". */
  function isGift(i) {
    return !!(i && i.properties && i.properties[GIFT_PROP]);
  }

  function giftLines(cart) {
    return cart.items.filter(isGift);
  }

  function addGift(tier) {
    var props = {};
    props[GIFT_PROP] = String(tier.cents);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(tier.variant), quantity: 1, properties: props }] })
    });
  }

  /* change.js by line key, so the right line goes even when the same variant
     is in the cart twice, once as a gift and once because they bought it. */
  function dropLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  /* Saying no to a free gift.

     Until 28 Sep 2026 removing one was impossible: the shopper hit the bin,
     the line went, this script saw an earned tier with no gift against it and
     put it straight back. From the other side of the screen that is a cart
     that will not let go of something, which is exactly what the complaint
     said.

     So the cart remembers. Two notes ride on the cart itself rather than in
     this tab's memory, because the drawer, the cart page and a reload are
     three different pages and a shopper uses all of them:

       _gifts_added    gifts this script has put in at some point
       _gifts_declined gifts the shopper took back out

     A gift that is earned, was added by us, and is now gone was removed on
     purpose. It goes on the declined note and is not offered again. Nothing
     else can tell those apart, since a first-time earn also looks like
     "earned but not in the cart".

     Both notes are wiped when the cart empties, so the next order starts
     clean and nobody is punished for a no they gave last week. */
  function noteList(cart, key) {
    var raw = (cart.attributes && cart.attributes[key]) || '';
    return String(raw).split(',').filter(Boolean);
  }

  function writeNotes(added, declined) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: {
        _gifts_added: added.join(','),
        _gifts_declined: declined.join(',')
      } })
    });
  }

  /* The unconditional sweep.

     Everything else in this file only runs when the reward bar is on the page,
     because the bar carries the ladder. That made the bar the single point of
     failure: no bar, no check, and a gift the shopper never earned rides all
     the way to checkout.

     On 29 Sep 2026 a customer found the gift products through search, added
     two, and was looking at a $0.00 subtotal with a live Checkout button.
     The product pages and the search results are closed now, but a cart can
     still be built by hand, so this is the lock that does not care how the
     line got there.

     Gift lines are found by handle, not by the ladder and not by the _reward
     property, because a line added by hand has no property and the ladder may
     not be on the page. Every gift product is handled gift-*, which is a
     naming rule the reward script itself relies on when it adds them.

     The rule is deliberately harsh: if entitlement cannot be PROVEN, the line
     goes. No bar means no proof, so it goes. Giving away stock on a maybe is
     worse than dropping a gift somebody can earn again by reloading. */
  function sweepUnearnedGifts() {
    return getCart().then(function (cart) {
      var gifts = cart.items.filter(isGift);
      if (!gifts.length) return false;

      var el = bar();
      var earned = {};
      if (el && el.getAttribute('data-gifts') === 'on') {
        var tiers = ladder(el);
        var spend = spendable(cart);
        tiers.forEach(function (t) {
          if (spend >= t.cents) earned[String(t.variant)] = true;
        });
      } else {
        /* No bar on this page, so entitlement cannot be read. Judging now
           would strip gifts somebody genuinely earned, purely because they
           happened to open a page that was rendered while the cart was empty.

           One case is still safe to act on: a cart holding nothing but gifts.
           Nothing in it could ever have earned them, whatever the ladder
           says, and that is exactly the shape of the cart the customer built
           on 29 Sep 2026. Everything else waits for a page that can judge,
           which is the next drawer render. */
        var realLines = cart.items.filter(function (i) {
          return !isGift(i);
        }).length;
        if (realLines > 0) return false;
      }

      var seen = {};
      var doomed = [];
      gifts.forEach(function (line) {
        var id = String(line.variant_id);
        if (!earned[id] || seen[id] || line.quantity !== 1) doomed.push(line.key);
        seen[id] = true;
      });
      if (!doomed.length) return false;

      /* A sweep is not a customer saying no.

         reconcile() decides someone declined a gift by seeing it in the
         _gifts_added note and no longer in the cart. If the sweep pulls a
         gift out and leaves that note alone, the next earn is read as a
         refusal and the gift is never given again. That is exactly what
         happened the first time this shipped: a cart at $225 got nothing.

         So anything the sweep takes is also forgotten. The shopper is back
         to never having had it, which is the truth. */
      var forget = {};
      gifts.forEach(function (line) {
        if (doomed.indexOf(line.key) !== -1) forget[String(line.variant_id)] = true;
      });
      var added = noteList(cart, '_gifts_added').filter(function (id) { return !forget[id]; });
      var declined = noteList(cart, '_gifts_declined').filter(function (id) { return !forget[id]; });

      var chain = Promise.resolve();
      doomed.forEach(function (key) {
        chain = chain.then(function () { return dropLine(key); });
      });
      chain = chain.then(function () { return writeNotes(added, declined); });
      return chain.then(function () { return true; });
    }).catch(function () { return false; });
  }

  function reconcile() {
    var el = bar();
    if (!el || el.getAttribute('data-gifts') !== 'on') return Promise.resolve(false);

    var tiers = ladder(el);
    if (!tiers.length) return Promise.resolve(false);

    return getCart().then(function (cart) {
      var have = giftLines(cart);
      var spend = spendable(cart);

      /* Every earned gift, minus any that a higher one already contains.

         This matters because of how Shopify applies automatic discounts. The
         gifts are no longer $0 products, they are the real pads and the real
         case made free by automatic Buy X Get Y rules, and Shopify will only
         ever apply ONE automatic product discount to an order. Proven on this
         store on 7 Oct 2026: a $225 cart holding both gifts had the case
         discounted and the pads quietly charged at $6.99.

         So the $200 rung hands over a single combined product, pads and case
         in one, and is marked "replaces". Earning it retires the $100 pads
         gift, which leaves exactly one discount to apply and nothing for
         Shopify to choose between. The shopper still receives both items. */
      var earned = tiers.filter(function (t) { return spend >= t.cents; });
      var topReplacer = earned.reduce(function (best, t) {
        return t.replaces && (!best || t.cents > best.cents) ? t : best;
      }, null);
      var wanted = earned.filter(function (t) {
        return !topReplacer || t.cents >= topReplacer.cents;
      });

      var added = noteList(cart, '_gifts_added');
      var declined = noteList(cart, '_gifts_declined');

      // An empty basket forgets everything, including any no given earlier.
      var realLines = cart.items.filter(function (i) {
        if (i.properties && i.properties[GIFT_PROP]) return false;
        return true;
      }).length;
      if (realLines === 0) {
        if (added.length || declined.length) {
          return writeNotes([], []).then(function () { return false; });
        }
        return false;
      }

      // Anything we put there that is no longer earned, or is a duplicate.
      var seen = {};
      var remove = [];
      have.forEach(function (line) {
        var owed = wanted.some(function (t) { return String(t.variant) === String(line.variant_id); });
        var key = String(line.variant_id);
        if (!owed || seen[key] || line.quantity !== 1) remove.push(line.key);
        seen[key] = true;
      });

      var add = [];
      var newlyDeclined = [];
      wanted.forEach(function (t) {
        var id = String(t.variant);
        if (declined.indexOf(id) !== -1) return;            // they already said no
        var inCart = have.some(function (line) { return String(line.variant_id) === id; });
        if (inCart) return;
        if (added.indexOf(id) !== -1) {
          // We put it in, it is earned, and it is gone. They took it out.
          newlyDeclined.push(id);
          return;
        }
        add.push(t);
      });

      var notesChanged = newlyDeclined.length > 0 || add.length > 0;
      var nextAdded = added.concat(add.map(function (t) { return String(t.variant); }));
      var nextDeclined = declined.concat(newlyDeclined);

      if (!remove.length && !add.length && !notesChanged) return false;

      // One at a time. Shopify serialises cart writes anyway and a burst of
      // parallel posts comes back with stale line keys.
      var chain = Promise.resolve();
      remove.forEach(function (key) {
        chain = chain.then(function () { return dropLine(key); });
      });
      add.forEach(function (t) {
        chain = chain.then(function () { return addGift(t); });
      });
      if (notesChanged) {
        chain = chain.then(function () { return writeNotes(nextAdded, nextDeclined); });
      }
      // A decline changes no line, so nothing on screen needs redrawing.
      return chain.then(function () { return remove.length > 0 || add.length > 0; });
    }).catch(function () { return false; });
  }

  /* A gift is one. Not two, not a hundred.

     The gift products are priced $0 and published, so anybody who knows a
     variant id can ask for 100 of them. Three orders did exactly that on
     6 and 7 Oct 2026, one of them taking 105 items for $4.95 of shipping.

     This puts any gift line back to a quantity of one before anything else
     runs. Clamping rather than deleting matters: the shopper may well have
     earned the gift honestly, and taking it away for touching the stepper
     is the bug that was reported on 28 Sep.

     This is a fence, not a wall. It cannot stop someone who posts to
     /cart/add.js and goes straight to /checkout without loading a page.
     Only a server side rule can do that. Xavier, 7 Oct 2026. */
  function clampGifts() {
    return getCart().then(function (cart) {
      var over = (cart.items || []).filter(function (i) {
        return isGift(i) && i.quantity > 1;
      });
      if (!over.length) return false;
      return over.reduce(function (chain, line) {
        return chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 1 })
          });
        });
      }, Promise.resolve()).then(function () { return true; });
    });
  }

  function run() {
    if (busy) { again = true; return; }
    busy = true;
    clampGifts().then(function (clamped) {
    return sweepUnearnedGifts().then(function (swept) {
      return dropOrphanProtection().then(function (dropped) {
        return reconcile().then(function (changed) { return changed || dropped || swept || clamped; });
      });
    });
    }).then(function (changed) {
      busy = false;
      if (again) { again = false; run(); return; }
      if (!changed) return;
      // The drawer redraws itself on this event. The cart page does not, so a
      // gift would land in the cart and the list on screen would not show it
      // until the shopper reloaded. Crossing a tier is rare, so reloading
      // there is the honest fix.
      if (/^\/cart(\/|$|\?)/.test(location.pathname)) {
        location.reload();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }).catch(function () {
      /* Never leave the engine jammed. busy is a lock, and without this a
         single rejected fetch held it closed for the rest of the page view,
         so every later add-to-cart was ignored and the shopper reached
         checkout with no gift and no clue why. Xavier, 7 Oct 2026. */
      busy = false;
      if (again) { again = false; run(); }
    });
  }

  /* ------------------------------------------- shipping protection -- */
  /*
    Speed is the whole job here. The old version read the cart, then wrote to
    it, then asked the drawer to redraw itself, three round trips before the
    switch looked like it had moved. It felt broken.

    Now: the switch flips on the click with nothing to wait for, ONE write
    goes out, and the only thing the answer is used for is the subtotal.
    /cart/update.js is the one endpoint that both adds and removes a line and
    hands back the whole cart, so there is nothing else to ask for.

    Nothing redraws, because the protection line is not in the list on screen
    (both cart sections skip it) and it does not count towards a spend tier.
    So the only number on screen that can change is the subtotal, and this
    writes it straight in. It appears at checkout like any other line.
  */
  /* The section this row lives in, named the way Shopify names it, so the
     write can bring the freshly rendered markup back with it. */
  function sectionIdFor(el) {
    var host = el.closest('.shopify-section, [id^="shopify-section-"]');
    if (host && host.id) return host.id.replace(/^shopify-section-/, '');
    if (el.closest('#cart-drawer')) return 'cart-drawer';
    return null;
  }

  var SUBTOTAL = '.ocd__subtotal-value, [data-ocd-subtotal], [data-ocp-subtotal]';

  /* Only the subtotal is swapped, never the whole panel. Replacing the panel
     is what made this slow before: it throws away and rebuilds every row, the
     essentials slider and the scroll position. The subtotal is the one number
     on screen that protection can change, because the line itself is kept out
     of the list and out of the spend tiers. */
  function paintSubtotal(html) {
    if (!html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector(SUBTOTAL);
    if (!fresh) return;
    Array.prototype.forEach.call(document.querySelectorAll(SUBTOTAL), function (el) {
      if (el.innerHTML !== fresh.innerHTML) el.innerHTML = fresh.innerHTML;
    });
  }

  document.addEventListener('click', function (event) {
    var pill = event.target.closest('.osp__pill');
    if (!pill) return;
    var row = pill.closest('[data-shipping-protection]');
    if (!row) return;
    var variant = Number(row.getAttribute('data-variant'));
    if (!variant) return;

    event.preventDefault();

    // A second tap while the first is still in flight would race it, so the
    // latest state wins and only one request is ever open.
    var turningOn = !row.classList.contains('is-on');
    row.classList.toggle('is-on', turningOn);
    pill.setAttribute('aria-checked', String(turningOn));

    if (row.__ospPending) { row.__ospWanted = turningOn; return; }
    row.__ospPending = true;

    var sectionId = sectionIdFor(row);

    function write(want) {
      var updates = {};
      updates[variant] = want ? 1 : 0;
      var body = { updates: updates };
      if (sectionId) body.sections = sectionId;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) throw new Error('cart update ' + r.status);
        return r.json();
      }).then(function (cart) {
        if (sectionId && cart.sections) paintSubtotal(cart.sections[sectionId]);
        document.dispatchEvent(new CustomEvent('cart:quiet-change', { detail: cart }));
        if (row.__ospWanted != null && row.__ospWanted !== want) {
          var next = row.__ospWanted;
          row.__ospWanted = null;
          return write(next);
        }
        row.__ospWanted = null;
      });
    }

    write(turningOn).catch(function () {
      // Put the switch back where it was and say so, rather than leaving a
      // tick on a cart that never got the line.
      row.__ospWanted = null;
      var real = !turningOn;
      row.classList.toggle('is-on', real);
      pill.setAttribute('aria-checked', String(real));
    }).then(function () {
      row.__ospPending = false;
    });
  });

  /* Protection on a cart with nothing left to protect. Empty the basket while
     the switch is on and Shopify keeps the line, so the shopper is looking at
     a $2.95 cart holding only the protection, and the row hides itself at zero
     real lines so they cannot even switch it off. Clear it. */
  /* Found by product handle, not by an id read off the page. An emptied cart
     renders its empty state, which draws neither the row nor the reward bar,
     so at the one moment this has to work there is nothing in the DOM to ask.
     cart.js always carries the handle. */
  var OSP_HANDLE = 'shipping-protection';

  function dropOrphanProtection() {
    return getCart().then(function (cart) {
      var mine = null, real = 0;
      cart.items.forEach(function (i) {
        if (i.handle === OSP_HANDLE) { mine = i; return; }
        if (i.properties && i.properties[GIFT_PROP]) return;   // a gift is not a reason to keep it
        real += 1;
      });
      if (!mine || real > 0) return false;
      return dropLine(mine.key).then(function () { return true; });
    }).catch(function () { return false; });
  }

  /* The checkout guard.

     Everything above reacts to cart events, which means there is a gap: drop
     from $135 to $45 and for a second or so the free pads are still a line in
     the cart. Click checkout inside that gap and you leave with a gift you no
     longer qualify for. Small money, but it is exactly the sort of thing a
     shopper finds by accident and then repeats on purpose.

     So the checkout button re-checks before it lets you through. It costs a
     few hundred milliseconds on the one click where being right matters.

     It NEVER blocks the shopper. If the check errors or is slow, a 1.5 second
     timer lets the click through anyway. A cart that is wrong is better than
     a customer who cannot pay.

     Not covered: the PayPal and Shop Pay buttons, which are cross origin
     iframes that no script here can intercept. The event listeners still
     clean up within a second or two, so that gap is narrower than it looks. */
  /* Shop Pay, PayPal and Apple Pay on the cart page are not the theme's own
     checkout button and carry no name="checkout", so they used to sail past
     this guard and take the shopper to payment with whatever was in the cart
     at that instant. They are caught now too. Xavier, 7 Oct 2026. */
  var CHECKOUT_SEL = '[name="checkout"], shopify-accelerated-checkout button,' +
    ' shopify-accelerated-checkout-cart button, .shopify-payment-button__button,' +
    ' [data-testid="Checkout-button"]';

  function guardedCheckout(event) {
    var btn = event.target.closest(CHECKOUT_SEL);
    if (!btn || btn.getAttribute('data-reward-checked') === '1') return;

    event.preventDefault();
    event.stopPropagation();
    btn.setAttribute('aria-busy', 'true');

    var released = false;
    function release() {
      if (released) return;
      released = true;
      btn.setAttribute('data-reward-checked', '1');
      btn.removeAttribute('aria-busy');
      btn.click();
    }

    setTimeout(release, 1500);
    Promise.all([sweepUnearnedGifts(), dropOrphanProtection(), reconcile()]).then(release, release);
  }

  document.addEventListener('click', guardedCheckout, true);

  /* Wait for the shopper to finish before touching anything.

     A customer emailed on 2 Oct 2026 with "no valid id or line parameter" on
     two cart lines, after trying to remove something. Reproduced here.

     The cause is two writers on the same cart at the same moment. The shopper
     clicks Remove, the theme POSTs the change and fires cart:change, and this
     script used to answer that event instantly. If the removal dropped the
     cart under a tier, this script started pulling the gift out while the
     theme was still rendering the response to the shopper's own request. The
     theme then re-rendered holding a line key that no longer existed, and
     printed Shopify's error onto the line.

     So the work is debounced. Cart events are noisy and arrive in bursts, and
     every one of them is a hint that the shopper is mid-action. Waiting for a
     short quiet gap means this script only ever writes to a settled cart, and
     the shopper's own change is always finished first.

     The delay is unnoticeable: adjusting a gift is not something anyone is
     waiting on, and the checkout button re-checks separately anyway. */
  var settleTimer = null;
  function runWhenSettled() {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(function () { settleTimer = null; run(); }, 600);
  }

  document.addEventListener('cart:change', runWhenSettled);
  document.addEventListener('cart:refresh', function () {
    // The bar has just been redrawn with a fresh data-spend. Check it, but do
    // not emit another refresh unless a line really changes.
    if (!busy) runWhenSettled();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
